import type { TranslationKeys } from "@/i18n/types";
import type { BreakRulesConfig, HoursRateConfig, NurseRole, OvertimeConfig } from "../models";
import { EmploymentType, RateHoursBonusType } from "../models";
import { getBreakRulesConfigReasons, getOvertimeConfigReasons } from "./validations";

/*
 * Form-shape → API-shape conversions for the three wage rule tables. Used by the Team page's Wage
 * Settings sections (the only edit surface since DAT-905) and by the department CREATE path, which
 * still seeds a new department with the default rows.
 *
 * Each throws on a row the backend would reject, with a message the caller surfaces as a snackbar.
 */

/**
 * Invariant guard, not validation: every caller disables Save on these same reasons (Team → Wage
 * Settings), or sends only the seeded defaults (the create path), so reaching the throw is a bug.
 * Users see the translated reasons in the Save tooltip, never this message.
 */
const assertValidRows = (table: string, reasons: TranslationKeys[]): void => {
	if (reasons.length > 0) throw new Error(`Invalid ${table} rows reached the request body: ${reasons.join(", ")}`);
};

/**
 * Transform hoursRateConfig: validate and transform for backend
 * Throws if any config has roles empty (must choose All or Specific) or invalid time ranges.
 */
export function transformHoursRateConfigsForBackend(
	configs: HoursRateConfig[] = [],
	deptRoles: NurseRole[] = [],
	deptJobIds: string[] = [],
): HoursRateConfig[] {
	if (!configs) return [];

	// Validate that all configs have roles defined (user must choose "All roles" or "Specific roles")
	const invalidRoleConfigs = configs.filter((config) => config.roles == null);
	if (invalidRoleConfigs.length > 0) {
		const configNames = invalidRoleConfigs.map((c) => c.name || "Unnamed").join(", ");
		throw new Error(`Please choose "All roles" or select specific roles for each hours rate configuration. Missing for: ${configNames}`);
	}
	// An unchosen agency must not be sent as [], which the backend stores as All agencies (DAT-1053).
	const invalidAgencyConfigs = configs.filter((config) => config.agency == null);
	if (invalidAgencyConfigs.length > 0) {
		const configNames = invalidAgencyConfigs.map((c) => c.name || "Unnamed").join(", ");
		throw new Error(`Please choose All or specific agencies for each hours rate configuration. Missing for: ${configNames}`);
	}
	// Validate time ranges. When startTime or endTime is null/undefined = "all day", skip time validation.
	const invalidTimeConfigs: Array<{ name: string; error: string }> = [];
	for (const config of configs) {
		if (config.startTime == null || config.endTime == null || config.startTime === "" || config.endTime === "") {
			// All day - skip time validation
			continue;
		}

		// Parse time strings (format: HH:MM or HH:MM:SS)
		const startParts = config.startTime.split(":").map((p) => parseInt(p, 10));
		const endParts = config.endTime.split(":").map((p) => parseInt(p, 10));

		if (startParts.length < 2 || endParts.length < 2) {
			invalidTimeConfigs.push({
				name: config.name || "Unnamed",
				error: "Invalid time format. Expected HH:MM",
			});
			continue;
		}

		const [startHour, startMin] = startParts;
		const [endHour, endMin] = endParts;

		// Check for NaN values (invalid parsing)
		if (Number.isNaN(startHour) || Number.isNaN(startMin) || Number.isNaN(endHour) || Number.isNaN(endMin)) {
			invalidTimeConfigs.push({
				name: config.name || "Unnamed",
				error: "Invalid time format. Hours and minutes must be numbers",
			});
			continue;
		}

		// Validate hours (0-23) and minutes (0-59)
		if (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23) {
			invalidTimeConfigs.push({
				name: config.name || "Unnamed",
				error: "Hours must be between 00 and 23",
			});
			continue;
		}

		if (startMin < 0 || startMin > 59 || endMin < 0 || endMin > 59) {
			invalidTimeConfigs.push({
				name: config.name || "Unnamed",
				error: "Minutes must be between 00 and 59",
			});
			continue;
		}

		// Validate that start time is not equal to end time
		if (startHour === endHour && startMin === endMin) {
			invalidTimeConfigs.push({
				name: config.name || "Unnamed",
				error: "Start time cannot equal end time",
			});
		}
	}

	if (invalidTimeConfigs.length > 0) {
		const errorMessages = invalidTimeConfigs.map((c) => `${c.name}: ${c.error}`).join("; ");
		throw new Error(`Invalid time ranges in hours rate configuration: ${errorMessages}`);
	}

	// Validate that all configs have bonusType (FlatRate or Percentage)
	const invalidBonusTypeConfigs = configs.filter(
		(config) => config.bonusType == null || (config.bonusType !== RateHoursBonusType.FlatRate && config.bonusType !== RateHoursBonusType.Percentage),
	);
	if (invalidBonusTypeConfigs.length > 0) {
		const configNames = invalidBonusTypeConfigs.map((c) => c.name || "Unnamed").join(", ");
		throw new Error(`Rate type (Flat Rate or Percentage) must be selected for each hours rate configuration. Please select for: ${configNames}`);
	}

	// Send roles ([] = All, [x,y] = Specific). When all dept roles selected, send [].
	// Same normalization for jobIds against the department's full job set.
	// For all day, send startTime/endTime as null.
	return configs.map((config) => {
		const { _specificRolesChosen, ...rest } = config as HoursRateConfig & { _specificRolesChosen?: boolean };
		const isAllDay = rest.startTime == null || rest.endTime == null || rest.startTime === "" || rest.endTime === "";
		const roles = rest.roles ?? [];
		const rolesToSend = deptRoles.length > 0 && roles.length === deptRoles.length ? [] : roles;
		const jobIds = rest.jobIds ?? [];
		const jobIdsToSend = deptJobIds.length > 0 && jobIds.length === deptJobIds.length ? [] : jobIds;
		return {
			...rest,
			roles: rolesToSend,
			jobIds: jobIdsToSend,
			bonusType: rest.bonusType,
			weekend: rest.weekend ?? null,
			startTime: isAllDay ? null : rest.startTime,
			endTime: isAllDay ? null : rest.endTime,
		};
	});
}

const ALL_EMPLOYMENT_TYPES = Object.values(EmploymentType);

export function transformOvertimeConfigsForBackend(
	configs?: OvertimeConfig[],
	deptRoles: NurseRole[] = [],
	deptJobIds: string[] = [],
): OvertimeConfig[] | undefined {
	if (!configs) return undefined;
	assertValidRows("overtime", getOvertimeConfigReasons(configs, deptRoles));
	return configs.map((c) => {
		const roles = c.roles ?? [];
		const rolesToSend = deptRoles.length > 0 && roles.length === deptRoles.length ? [] : roles;
		const empTypes = Array.isArray(c.employmentType) ? c.employmentType : [];
		const employmentTypeToSend = empTypes.length === ALL_EMPLOYMENT_TYPES.length ? [] : empTypes;
		const jobIds = c.jobIds ?? [];
		const jobIdsToSend = deptJobIds.length > 0 && jobIds.length === deptJobIds.length ? [] : jobIds;
		return {
			...c,
			roles: rolesToSend,
			jobIds: jobIdsToSend,
			rateType: c.rateType ?? RateHoursBonusType.FlatRate,
			employmentType: employmentTypeToSend,
		};
	});
}

export function transformBreakRulesConfigsForBackend(
	configs?: BreakRulesConfig[],
	deptRoles: NurseRole[] = [],
	deptJobIds: string[] = [],
): BreakRulesConfig[] | undefined {
	if (!configs) return undefined;
	assertValidRows("break rules", getBreakRulesConfigReasons(configs, deptRoles));
	return configs.map((c) => {
		const roles = c.roles ?? [];
		const rolesToSend = deptRoles.length > 0 && roles.length === deptRoles.length ? [] : roles;
		const empTypes = Array.isArray(c.employmentTypes) ? c.employmentTypes : [];
		const employmentTypesToSend = empTypes.length === ALL_EMPLOYMENT_TYPES.length ? [] : empTypes;
		const jobIds = c.jobIds ?? [];
		const jobIdsToSend = deptJobIds.length > 0 && jobIds.length === deptJobIds.length ? [] : jobIds;
		return {
			...c,
			roles: rolesToSend,
			jobIds: jobIdsToSend,
			employmentTypes: employmentTypesToSend,
		};
	});
}

/**
 * The overtime / break-rules transforms return `undefined` only for an absent list (the create form's
 * "leave the column alone"). A section always passes its rows, so `undefined` here is a bug.
 */
export const requireTransformedRows = <TRow>(rows: TRow[] | undefined): TRow[] => {
	if (rows === undefined) throw new Error("Wage rule rows were not transformed for the backend.");
	return rows;
};
